import { graphql } from "gatsby";
import { useIntl } from "../../hooks/useIntl";
import React from "react";
import { useCallback } from "react";
import Page from "../../components/Page";
import { getTranslations } from "../../helpers/translations";
import SEO from "../../components/SEO";
import { parseSEO } from "../../helpers/seo";
import {
  FoodServiceFormProps,
  InternationalSalesForm,
  InternationalSalesFormProps,
} from "../../components/ContactUs";
import { useStaticLabels } from "../../hooks/useStaticLabels";
import { isPublished } from "../../helpers/directus";

const InternationalSales = ({ data }: { data: any }) => {
  const intl = useIntl();
  const labels = useStaticLabels();

  const getRootTranslatedData = useCallback(() => {
    return getTranslations(
      data,
      "directus.contact_us_page.translations",
      intl.locale
    );
  }, [data]);

  const getInternationalSalesData = useCallback(() => {
    return getTranslations(
      getRootTranslatedData(),
      "international_inquiries_form.translations",
      intl.locale
    );
  }, [getRootTranslatedData]);

  if (!isPublished(getRootTranslatedData()?.international_inquiries_status))
    return null;

  const getFormData = useCallback((): InternationalSalesFormProps => {
    const t = getInternationalSalesData();

    return {
      backLabel: labels?.backToContactUs,
      internationalInquiry: {
        successMessage: t?.success_message,
        disclaimer: t?.disclaimer,
        button: t?.button_label,
        distributorsPlaceholder: t?.distributors_placeholder,
        productsPlaceholder: t?.products_placeholder,
        countryPlaceholder: labels?.countryPlaceholder,
        businessNamePlaceholder: labels?.businessNamePlaceholder,
        phonePlaceholder: labels?.phonePlaceholder,
        emailPlaceholder: labels?.emailPlaceholder,
        firstNamePlaceholder: labels?.firstNamePlaceholder,
        lastNamePlaceholder: labels?.lastNamePlaceholder,
        klaviyoId: t?.klaviyo_id,
        title: t?.title,
      },
    };
  }, [getInternationalSalesData]);

  const getLabels = useCallback((): FoodServiceFormProps => {
    const t = getTranslations(
      data,
      "directus.contact_us_page.main_section.translations",
      intl.locale
    );
    return {
      backLabel: t?.back_to_contact_us_label,
    };
  }, [data, intl]);

  return (
    <Page
      initialHeaderBackgroundColor={"var(--color-green-90)"}
      helmet={
        <SEO
          {...parseSEO(
            getInternationalSalesData(),
            intl?.locale,
            null,
            "seo.translations"
          )}
        />
      }
    >
      <InternationalSalesForm {...getLabels()} {...getFormData()} />
    </Page>
  );
};

export default InternationalSales;

export const query = graphql`
  query ($locale: String) {
    directus {
      contact_us_page {
        translations(filter: { languages_code: { code: { _eq: $locale } } }) {
          languages_code {
            code
            name
          }
          status
          international_inquiries_status
          international_inquiries_form {
            translations(
              filter: { languages_code: { code: { _eq: $locale } } }
            ) {
              seo {
                ...SEO
              }
              id
              success_message
              disclaimer
              languages_code {
                code
                name
              }
              title
              klaviyo_id
              products_placeholder
              distributors_placeholder
              button_label
            }
          }
        }
      }
    }
  }
`;
